import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Sparkles, ChevronLeft } from 'lucide-react';
import Header from './Header';
import ArchiveStrip from './ArchiveStrip';
import ClueCardSimple from './ClueCardSimple';
import SEO from './SEO';
import { usePuzzles } from '../context/PuzzleContext';
import NotFoundPage from './NotFoundPage';

import Sidebar from './Sidebar';

const PuzzlePage = ({ isToday, date: propDate }) => {
  const params = useParams();
  const { puzzles, loading } = usePuzzles();
  
  const groupedPuzzles = useMemo(() => {
    const groups = {};
    puzzles.forEach(puzzle => {
      const date = new Date(puzzle.date);
      const key = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!groups[key]) {
        groups[key] = [];
      }
      // Only keep up to 4 puzzles per month to keep it clean
      if (groups[key].length < 4) {
        groups[key].push(puzzle);
      }
    });
    return groups;
  }, [puzzles]);
  
  if (loading) return null; // Or a loading spinner

  // If isToday, use the passed prop date (latest), otherwise use the URL param
  let selectedPuzzle = null;

  if (isToday) {
    selectedPuzzle = puzzles.find(p => p.date === propDate);
  } else {
    // Try to find by number if URL matches pocket-grid-X
    const numberMatch = params.puzzleId ? params.puzzleId.match(/pocket-grid-(-?\d+)/) : null;
    if (numberMatch) {
      const number = numberMatch[1];
      selectedPuzzle = puzzles.find(p => p.puzzleNumber === number);
    } else {
        // Fallback for legacy or direct date access if needed
         selectedPuzzle = puzzles.find(p => p.date === params.date);
    }
  }
  
  // If no puzzle found for that date, redirect to home
  if (!selectedPuzzle && !isToday) return <NotFoundPage />;
  // Fallback if propDate failed for some reason (shouldn't happen with correct routing)
  if (!selectedPuzzle) return null; 

  const { date, title, clues } = selectedPuzzle;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": isToday ? `Today's Pocket Grids Hints & Answers: ${title}` : `${title} Hints and Answers`,
    "description": `Get hints and answers for the ${title} from ${new Date(date).toLocaleDateString()}. Zero spoilers mode available.`,
    "image": `${window.location.origin}/pocket-grids-logo.png`,
    "datePublished": new Date(date).toISOString(),
    "dateModified": new Date(date).toISOString(),
    "author": {
      "@type": "Organization",
      "name": "PocketHints"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PocketHints",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/site-logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  };

  const breadcrumbs = [
    { name: "Home", item: window.location.origin },
    { name: "Pocket Grids Archive", item: `${window.location.origin}/archive` },
    { name: isToday ? "Today's Puzzle" : `${title}`, item: window.location.href }
  ];

  return (
    <div className="flex flex-col w-full pb-12">
      <SEO 
        title={isToday ? "Today's Pocket Grids Hints & Answers" : `${title} - ${new Date(date).toLocaleDateString()}`}
        description={`Get hints and answers for the ${title} from ${new Date(date).toLocaleDateString()}. Zero spoilers mode available.`}
        url={window.location.href}
        schema={schema}
        breadcrumbs={breadcrumbs}
      />
      <Header date={date} title={title} />
      
      {/* Archive Strip */}
      <ArchiveStrip 
        puzzles={puzzles} 
        selectedDate={date} 
      />


      {/* Conditional SEO Text - Only for "Today's" view */}
      {isToday && (
        <div className="max-w-4xl mx-auto px-4 mb-12 text-center">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              Daily Pocket Grids Hints & Answers
            </h2>
            <div className="prose prose-blue mx-auto text-gray-600 leading-relaxed mb-8 entry-content">
              <p className="mb-4">
                <strong>Today's Pocket Grids Answers</strong> are just a click away! Are you almost done with your daily dose of wordplay? 
                Let's decode the last clues together! Our daily guide to the Pocket Grids will help you finish today's edition—and every day's puzzle—in no time. 
                Whether you're a serious puzzle fan or just love a good challenge, this page is your go-to for quick answers and tips.
              </p>
              <p className="mb-4">
                Pocket Grids is a shorter and more accessible version of the traditional word puzzle, 
                designed to be completed quickly. It typically features a concise grid and a set of clues that are often playful or punny. 
                Its brevity makes it suitable for a quick daily mental exercise.
              </p>
              <p className="text-xs text-gray-400 italic mt-4">
                Disclaimer: We are not affiliated with the creators of Pocket Grids. We just love the game!
              </p>
            </div>

            <div className="text-center">
              <a 
                href="https://www.reddit.com/r/pocketgrids/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-500 hover:scale-105 transition-all duration-200"
              >
                Play Pocket Grids on Reddit
              </a>
              <p className="mt-3 text-xs font-medium text-gray-400 uppercase tracking-wide">
                Join the Community
              </p>
            </div>
          </div>
        </div>
      )}
      
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 w-full flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            
            {/* Across Clues */}
          <section className="w-full relative">
            <div className="sticky top-24 z-30 bg-white border-b border-gray-200 py-3 mb-6 lg:static lg:bg-transparent lg:border-none rounded-xl lg:rounded-none px-4 lg:px-0 shadow-sm lg:shadow-none">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-900 text-white text-sm font-bold shadow-sm">A</span>
                Across
              </h2>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed min-h-[4.5rem]">
              Tackle your daily grid systematically—"Across" clues run horizontally from left to right. Need a hand? Fill in the gaps with the ACROSS clues and answers for today's Pocket Grids puzzle below.
            </p>


            <div className="space-y-4">
              {clues.across.map((clue) => (
                <ClueCardSimple 
                  key={`${date}-${clue.id}`} 
                  clue={clue} 
                  date={isToday ? null : date} 
                  isToday={isToday}
                  puzzleNumber={selectedPuzzle.puzzleNumber}
                />
              ))}
            </div>
          </section>

          {/* Down Clues */}
          <section className="w-full relative">
            <div className="sticky top-24 z-30 bg-white border-b border-gray-200 py-3 mb-6 lg:static lg:bg-transparent lg:border-none rounded-xl lg:rounded-none px-4 lg:px-0 shadow-sm lg:shadow-none">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                 <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-yellow text-yellow-900 text-sm font-bold shadow-sm">D</span>
                 Down
              </h2>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed min-h-[4.5rem]">
              "Down" clues run vertically from top to bottom. Use the DOWN clues and answers below to build your grid, one column at a time. The answers are revealed progressively.
            </p>


            <div className="space-y-4">
              {clues.down.map((clue) => (
                <ClueCardSimple 
                  key={`${date}-${clue.id}`} 
                  clue={clue} 
                  date={isToday ? null : date}
                  isToday={isToday}
                  puzzleNumber={selectedPuzzle.puzzleNumber}
                />
              ))}
            </div>
          </section>

          </div>


          {/* Play More Section */}
          <section className="mt-8 mb-8 text-center bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center gap-6">
              <img 
                src={process.env.PUBLIC_URL + '/pocket-grids-logo.png'}
                alt="Pocket Grids Logo" 
                className="w-24 h-24 object-contain drop-shadow-sm"
              />
              <h2 className="text-3xl font-black text-gray-900">
                Want to Play More?
              </h2>
              <div className="flex flex-col items-center justify-center gap-4 w-full max-w-md">
                <Link 
                  to="/todays-syllo-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/Syllo-logo.png'} alt="Syllo" className="w-8 h-8 object-contain" />
                    <span>Syllo Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/todays-hot-and-cold-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/hotandcold-logo.png'} alt="Hot and Cold" className="w-8 h-8 object-contain" />
                    <span>Hot & Cold Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/todays-byrdle-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/byrdle-logo.png'} alt="Byrdle" className="w-8 h-8 object-contain" />
                    <span>Byrdle Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/daily-guess-solver"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/dailyguess-logo.png'} alt="DailyGuess" className="w-8 h-8 object-contain" />
                    <span>DailyGuess Solver</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
              </div>
            </div>
          </section>

          {/* SEO Content Section */}
          <section className="mt-12 bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-6">About Pocket Grids</h2>
            
            <div className="space-y-8 entry-content">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">How does Pocket Grids work?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Pocket Grids is a daily mini crossword puzzle designed to be completed quickly. It features a concise grid with a set of clever, often punny clues. You solve it just like a traditional crossword, filling in the "Across" and "Down" answers to complete the grid.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Why we do this</h3>
                <p className="text-gray-600 leading-relaxed">
                  We love the quick mental workout that Pocket Grids provides, but sometimes a single tricky clue can halt your progress. We created this helper site to offer progressive hints for each clue. Our goal is to give you a gentle nudge in the right direction so you can finish the puzzle without feeling like you cheated.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-gray-800">When does the new Pocket Grids puzzle come out?</h4>
                    <p className="text-gray-600">A new puzzle is released every day at 1 PM UTC.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">Are the puzzles always the same size?</h4>
                    <p className="text-gray-600">Pocket Grids are designed to be "mini" crosswords, so they are generally small, but the exact dimensions can vary slightly from day to day.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Archive Section */}
          <section className="mt-12 bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-gray-900 mb-4">Pocket Grids Archive</h2>
              <p className="text-gray-600 text-lg">Browse our collection of past daily crossword guides.</p>
            </div>
            <div className="space-y-12">
              {Object.entries(groupedPuzzles).slice(0, 3).map(([monthYear, monthPuzzles]) => (
                <div key={monthYear} className="animate-fade-in-up">
                  <h3 className="text-xl font-bold text-gray-400 uppercase tracking-wider mb-6 border-b border-gray-200 pb-2">
                    {monthYear}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {monthPuzzles.map((puzzle) => {
                       const pDate = new Date(puzzle.date);
                       const dayName = pDate.toLocaleDateString('en-US', { weekday: 'long' });
                       const dayNum = pDate.getDate();
                       
                       return (
                        <Link 
                          key={puzzle.date} 
                          to={`/pocket-grid-${puzzle.puzzleNumber || puzzle.date}`}
                          className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-blue hover:shadow-md transition-all flex items-center justify-between"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-brand-blue/10 text-brand-blue font-bold text-xl w-12 h-12 rounded-lg flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                              {dayNum}
                            </div>
                            <div>
                              <div className="font-bold text-gray-900">{puzzle.title}</div>
                              <div className="text-sm text-gray-500 font-medium">{dayName}</div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-brand-blue transition-colors" />
                        </Link>
                       );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-10 text-center">
              <Link 
                to="/archive"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-100 text-gray-900 text-lg font-bold rounded-xl shadow-sm hover:bg-gray-200 hover:scale-105 transition-all duration-200"
              >
                View Full Archive
              </Link>
            </div>
          </section>
        </div>

        {/* Sidebar (Desktop Only) */}
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div>
            <Sidebar />
          </div>
        </div>
      </main>
    </div>
  );
};

export default PuzzlePage;
